import React, { useState, useEffect } from 'react';
import './ProductPage.css';
import { AiFillHome } from 'react-icons/ai';
import { MdRestaurantMenu } from 'react-icons/md';
import { FaMoneyBillWave, FaUserFriends, FaGift } from 'react-icons/fa';
import { GiKnifeFork } from 'react-icons/gi';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

const navItems = [
  'Home',
  'Product',
  'Sales',
  'Members',
  'Promotion',
  'Restaurant',
];

const promoOptions = ['All', 'WELCOME GIFT', 'B1F1', 'WWGIFT', 'NONE'];

const navIcons = {
  Home: <AiFillHome className="nav-icon" />,
  Product: <MdRestaurantMenu className="nav-icon" />,
  Sales: <FaMoneyBillWave className="nav-icon" />,
  Members: <FaUserFriends className="nav-icon" />,
  Promotion: <FaGift className="nav-icon" />,
  Restaurant: <GiKnifeFork className="nav-icon" />,
};

const categoryIdMap = {
  'rice': 13,
  'noodle': 14,
  'drinks': 15,
  'fast-order': 16
};

function ProductPage({ onNavigate }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPromo, setSelectedPromo] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const itemsPerPage = 10;
                                
  // 动态生成分类选项
  const categorySet = new Set(products.map(p => p.category));
  const dynamicCategories = ['All', ...Array.from(categorySet)];

  // 动态生成promo code筛选选项
  const promoSet = new Set(products.map(p => p.promo_code));
  const dynamicPromoOptions = ['All', ...Array.from(promoSet)];

  // useEffect 只在首次加载时拉取所有数据
  useEffect(() => {
    fetchProducts(); // 只调用fetchProducts
  }, []);

  useEffect(() => {
    if (products.length > 0) {
      console.log('产品原始数据:', products);
    }
  }, [products]);

  // fetchProducts 优化，适配itemList结构
  const fetchProducts = async () => {
    setLoading(true);
    setProducts([]);
    setError(null);
      try {
      const res = await fetch('http://localhost:8080/catalog/magento/all');
        if (res.ok) {
          const data = await res.json();
        // 适配后端返回的itemList数组
        setProducts(data.itemList || []);
      } else {
        setError('产品数据加载失败: ' + res.statusText);
        }
      } catch (err) {
        setError('产品数据加载失败: ' + err.message);
      } finally {
      setLoading(false);
      }
  };

  const handleNavClick = (item) => {
    if (onNavigate) {
      onNavigate(item);
    }
  };

  const handleAddProduct = () => {
    // 跳转到ProductForm页面
    if (onNavigate) {
      onNavigate('ProductForm');
    }
  };

  // 分类筛选兼容多种情况（categoryId为主）
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase()));
    // 用categoryId字段与categoryIdMap比对
    const matchesCategory = selectedCategory === 'All'
      || product.categoryId === categoryIdMap[selectedCategory];
    const matchesPromo = selectedPromo === 'All' || (product.promo_code === selectedPromo);
    return matchesSearch && matchesCategory && matchesPromo;
  });

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleProductDetail = (product) => {
    setSelectedProduct(product);
    setShowDetailModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('确定要删除这个产品吗？')) {
      try {
        // 这里应为删除单个产品的API
        const response = await fetch(`http://localhost:8080/api/products/${productId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          setProducts(products.filter(p => p.id !== productId));
          alert('产品删除成功！');
        } else {
          throw new Error('删除产品失败');
        }
      } catch (error) {
        console.error('删除产品时出错:', error);
        alert(`删除产品失败: ${error.message}`);
      }
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'noodle': '#ff6b6b',
      'rice': '#4ecdc4',
      'chicken': '#45b7d1',
      'seafood': '#96ceb4',
      'drink': '#feca57'
    };
    return colors[category] || '#95a5a6';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'green';
      case 'draft': return 'orange';
      case 'archived': return 'red';
      case 'scheduled': return 'blue';
      default: return 'gray';
    }
  };

  if (error) {
    return (
      <div className="productpage-container">
        <div className="error">错误: {error}</div>
      </div>
    );
  }

  return (
    <div className="productpage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li 
              key={item} 
              className={item === 'Product' ? 'active' : ''}
              onClick={() => handleNavClick(item)}
            >
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>
      
      <main className="productpage-main">
        <div className="productpage-header">
          <div className="header-left">
            <h1>Products</h1>
            <p>Manage your restaurant products</p>
          </div>
          <div className="header-right">
            <button className="add-product-btn" onClick={handleAddProduct}>
              + Add Product
            </button>
          </div>
        </div>

        <div className="productpage-content">
          {/* 筛选器 */}
          <div className="filters">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-options">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="All">All</option>
                <option value="rice">Rice</option>
                <option value="noodle">Noodle</option>
                <option value="drinks">Drinks</option>
                <option value="fast-order">Fast Order</option>
              </select>
              
              <select
                value={selectedPromo}
                onChange={(e) => setSelectedPromo(e.target.value)}
              >
                {dynamicPromoOptions.map((option, idx) => (
                  <option key={option || `promo-${idx}`} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 产品网格 */}
          <div className="products-grid">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <div className="product-card" key={`skeleton-${i}`}> 
                    <div className="product-image">
                      <Skeleton height={120} style={{ marginBottom: 8 }} />
                    </div>
                    <div className="product-info">
                      <h3><Skeleton width={80} /></h3>
                      <p className="product-description"><Skeleton count={2} /></p>
                      <div className="product-meta">
                        <span className="price"><Skeleton width={40} /></span>
                      </div>
                      <div className="product-tags">
                        <Skeleton width={60} height={18} style={{ marginRight: 8 }} />
                        <Skeleton width={60} height={18} />
                      </div>
                    </div>
                  </div>
                ))
              : currentProducts.map((product, idx) => {
                  console.log('商品详情', product);
                  return (
                  <div key={product.id ? product.id : `product-${idx}`} className="product-card">
                    <div className="product-image">
                      <img
                        src={
                          product.image ||
                          (product.media_gallery_entries && product.media_gallery_entries.length > 0
                            ? 'https://order-dev.posjoy.com/pub/media/catalog/product' + product.media_gallery_entries[0].file
                            : '/images/default-product.jpg')
                        }
                        alt={product.name}
                      />
                      <div className="product-overlay">
                        <button 
                          className="view-btn"
                          onClick={() => handleProductDetail(product)}
                        >
                          View Details
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteProduct(product.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    
                    <div className="product-info">
                      <h3>{product.name}</h3>
                      <p className="product-description">{product.description}</p>
                      
                      <div className="product-meta">
                        <span className="price">¥{product.price}</span>
                        {product.membership_enabled && product.membership_price !== undefined && product.membership_price !== null && (
                          <span className="membership-price-badge">Member: ¥{product.membership_price}</span>
                        )}
                      </div>
                      <div className="product-tags">
                        <span className="promo-code-badge">{product.promo_code}</span>
                        <span className={`visibility-badge ${product.visibility}`}>{product.visibility === 'visible' ? 'Visible' : 'Hidden'}</span>
                        <span className={`seasonal-badge ${product.seasonal}`}>{product.seasonal === 'seasonal' ? 'Seasonal' : 'Regular'}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* 分页 */}
          {totalPages > 1 && !loading && (
            <div className="pagination">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page, idx) => (
                <button
                  key={page || `page-${idx}`}
                  onClick={() => handlePageChange(page)}
                  className={currentPage === page ? 'active' : ''}
                >
                  {page}
                </button>
              ))}
              
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </main>

      {/* 产品详情模态框 */}
      {showDetailModal && selectedProduct && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedProduct.name}</h2>
              <button 
                className="close-btn"
                onClick={() => setShowDetailModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="product-detail-image">
                <img
                  src={
                    selectedProduct.image ||
                    (selectedProduct.media_gallery_entries && selectedProduct.media_gallery_entries.length > 0
                      ? 'https://order-dev.posjoy.com/pub/media/catalog/product' + selectedProduct.media_gallery_entries[0].file
                      : '/images/default-product.jpg')
                  }
                  alt={selectedProduct.name}
                />
              </div>
              
              <div className="product-detail-info">
                <p><strong>Description:</strong> {selectedProduct.description}</p>
                <p><strong>Price:</strong> ${selectedProduct.price}</p>
                <p><strong>Category:</strong> {selectedProduct.category}</p>
                <p><strong>Status:</strong> {selectedProduct.status || 'published'}</p>
                <p><strong>Created:</strong> {new Date(selectedProduct.created_at).toLocaleDateString()}</p>
                <p><strong>Member Price:</strong> {selectedProduct.membership_enabled ? `¥${selectedProduct.membership_price}` : 'Disabled'}</p>
                <p><strong>Promo Code:</strong> {selectedProduct.promo_code}</p>
                <p><strong>Visibility:</strong> {selectedProduct.visibility === 'visible' ? 'Visible' : 'Hidden'}</p>
                <p><strong>Seasonal:</strong> {selectedProduct.seasonal === 'seasonal' ? 'Seasonal' : 'Regular'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductPage; 